import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  Brain,
  ChevronRight,
  Circle,
  FileText,
  Loader2,
  Monitor,
  Pencil,
  Search,
  Terminal,
  type LucideIcon,
} from "lucide-react";
import type { TimelineItem } from "../types.js";

/** Extract a /workspace path from a link href (so it opens in the file viewer). */
export function toWorkspacePath(href?: string): string | null {
  if (!href) return null;
  let h = href;
  if (h.startsWith("file://")) h = h.slice(7);
  else if (h.startsWith("file:")) h = h.slice(5);
  return h.startsWith("/workspace") ? h : null;
}

/** A rendered block: either a standalone message, or a group of work steps. */
type Block =
  | { kind: "msg"; item: TimelineItem }
  | { kind: "work"; key: string; items: TimelineItem[]; start: number; end: number };

const WORK_KINDS = new Set(["tool", "status", "screenshot", "thinking"]);

function buildBlocks(items: TimelineItem[]): Block[] {
  const blocks: Block[] = [];
  for (const it of items) {
    if (WORK_KINDS.has(it.kind)) {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "work") {
        last.items.push(it);
        last.end = it.ts;
      } else {
        blocks.push({ kind: "work", key: it.id, items: [it], start: it.ts, end: it.ts });
      }
    } else {
      blocks.push({ kind: "msg", item: it });
    }
  }
  return blocks;
}

function fmtDuration(ms: number): string {
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function stepIcon(it: TimelineItem): LucideIcon {
  if (it.kind === "screenshot") return Monitor;
  const t = it.text.toLowerCase();
  if (it.phase === "error") return AlertTriangle;
  if (t.startsWith("bash:") || t.startsWith("running:")) return Terminal;
  if (t.startsWith("read:")) return FileText;
  if (t.startsWith("write:") || t.startsWith("edit:")) return Pencil;
  if (t.startsWith("searching")) return Search;
  if (t.startsWith("taking over") || t.startsWith("computer")) return Monitor;
  return Circle;
}

export function Timeline({
  items,
  busy,
  onOpenFile,
}: {
  items: TimelineItem[];
  busy: boolean;
  onOpenFile: (path: string) => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the user was pinned to (within 24px of) the bottom before new content.
  const atBottomRef = useRef(true);
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
  };

  // Only auto-scroll if the user was already at the bottom; otherwise leave them
  // where they scrolled to, even as new messages stream in.
  // Re-run on any items change (incl. streaming text updates) so it follows along
  // while the user is pinned to the bottom.
  useEffect(() => {
    if (atBottomRef.current) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items]);

  const blocks = buildBlocks(items);
  const lastWorkKey = [...blocks].reverse().find((b) => b.kind === "work") as
    | (Block & { kind: "work" })
    | undefined;

  return (
    <div className="timeline" ref={scrollRef} onScroll={onScroll}>
      <div className="tl-inner">
        {items.length === 0 && (
          <div className="timeline-empty">
            Ask Atlas to build features, fix bugs, run commands, search the web, or operate
            the desktop — by voice or text.
          </div>
        )}

        {blocks.map((b) => {
          if (b.kind === "msg") {
            const it = b.item;
            if (it.kind === "you") {
              return (
                <div key={it.id} className="item item-you">
                  {it.text}
                </div>
              );
            }
            // assistant + result render as markdown (links to /workspace files
            // open in the in-app viewer; other links open in a new tab).
            return (
              <div key={it.id} className="item item-agent md">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => {
                      const wp = toWorkspacePath(href);
                      if (wp) {
                        return (
                          <a
                            className="filelink"
                            href={href}
                            onClick={(e) => {
                              e.preventDefault();
                              onOpenFile(wp);
                            }}
                          >
                            {children}
                          </a>
                        );
                      }
                      return (
                        <a href={href} target="_blank" rel="noreferrer">
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {it.text}
                </ReactMarkdown>
              </div>
            );
          }

          const active = busy && lastWorkKey?.key === b.key;
          // Default to expanded so tool activity is visible; user can collapse.
          const isOpen = b.key in toggled ? toggled[b.key] : true;
          const label = active ? "Working…" : `Worked for ${fmtDuration(b.end - b.start)}`;
          return (
            <div key={b.key} className={`work ${active ? "active" : ""}`}>
              <button
                className="work-head"
                onClick={() => setToggled((t) => ({ ...t, [b.key]: !isOpen }))}
              >
                <ChevronRight size={14} className={`work-chevron ${isOpen ? "open" : ""}`} />
                {active && <Loader2 size={12} className="work-spinner-icon" />}
                <span className="work-label">{label}</span>
                <span className="work-count">{b.items.length} steps</span>
              </button>
              {isOpen && (
                <div className="work-body">
                  {b.items.map((it) => {
                    if (it.kind === "screenshot" && it.url) {
                      return (
                        <a key={it.id} className="step shot-step" href={it.url} target="_blank" rel="noreferrer">
                          <img className="shot" src={it.url} alt="screenshot" />
                        </a>
                      );
                    }
                    if (it.kind === "thinking") {
                      const tOpen = it.id in toggled ? toggled[it.id] : false;
                      const tLabel = it.durationMs
                        ? `Thought for ${fmtDuration(it.durationMs)}`
                        : "Thinking…";
                      return (
                        <div key={it.id} className="think">
                          <button
                            className="think-head"
                            onClick={() => setToggled((t) => ({ ...t, [it.id]: !tOpen }))}
                          >
                            <ChevronRight
                              size={13}
                              className={`work-chevron ${tOpen ? "open" : ""}`}
                            />
                            <Brain size={13} className="step-ico think-ico" />
                            <span className="step-text think-label">{tLabel}</span>
                          </button>
                          {tOpen && (
                            <div className="think-body md">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.text}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      );
                    }
                    const Ico = stepIcon(it);
                    return (
                      <div key={it.id} className={`step ${it.phase === "error" ? "err" : ""}`}>
                        <Ico size={13} className="step-ico" />
                        <span className="step-text">{it.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
    </div>
  );
}
